import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Phone, 
  FileText,
  AlertCircle,
  CheckCircle,
  CreditCard
} from 'lucide-react';
import { API_BASE_URL } from '../../config/api';

interface PracticeSubscription {
  practice_id: string;
  practice_name: string;
  subscription_status: string;
  monthly_fixed_total: number;
  current_period_usage_cost: number;
  estimated_monthly_total: number;
  voice_calls_minutes: number;
  visit_recordings_count: number;
}

interface AdminMetrics {
  total_active_subscriptions: number;
  total_monthly_recurring_revenue: number;
  total_estimated_usage_revenue: number;
  total_voice_minutes: number;
  total_visit_recordings: number;
  practices: PracticeSubscription[];
}

const BillingAdminDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminMetrics();
  }, []);

  const fetchAdminMetrics = async () => {
    try {
      setLoading(true);
      // TODO: Create this admin endpoint
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/billing/metrics`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch admin metrics');
      }

      const data = await response.json();
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
      // Mock data for development
      setMetrics({
        total_active_subscriptions: 0,
        total_monthly_recurring_revenue: 0,
        total_estimated_usage_revenue: 0,
        total_voice_minutes: 0,
        total_visit_recordings: 0,
        practices: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      past_due: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Past Due' },
      canceled: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Canceled' },
      none: { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: 'No Subscription' },
    };

    const statusConfig = config[status as keyof typeof config] || config.none;
    const Icon = statusConfig.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {statusConfig.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-900 font-semibold">Error Loading Admin Dashboard</h3>
            <p className="text-red-700 text-sm mt-1">{error}</p>
            <p className="text-red-600 text-xs mt-2">Note: Admin billing metrics endpoint not yet implemented</p>
          </div>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Billing Administration</h1>
        <p className="text-gray-600">
          View and manage subscriptions across all practices
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total MRR */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-sm text-gray-600">MRR</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ${metrics.total_monthly_recurring_revenue.toFixed(2)}
          </p>
          <p className="text-xs text-gray-600 mt-1">Monthly Recurring Revenue</p>
        </div>

        {/* Estimated Usage Revenue */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-sm text-gray-600">Usage</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ${metrics.total_estimated_usage_revenue.toFixed(2)}
          </p>
          <p className="text-xs text-gray-600 mt-1">Current Period Usage</p>
        </div>

        {/* Active Subscriptions */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <span className="text-sm text-gray-600">Practices</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {metrics.total_active_subscriptions}
          </p>
          <p className="text-xs text-gray-600 mt-1">Active Subscriptions</p>
        </div>

        {/* Total Usage Stats */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Phone className="w-6 h-6 text-orange-600" />
            </div>
            <span className="text-sm text-gray-600">Usage</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {Math.floor(metrics.total_voice_minutes / 60)}h
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {metrics.total_visit_recordings} recordings
          </p>
        </div>
      </div>

      {/* Practices Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Practice Subscriptions</h2>
        </div>

        {metrics.practices.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No active subscriptions yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Practice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monthly Fixed
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage (This Period)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Voice Minutes
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recordings
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estimated Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {metrics.practices.map((practice) => (
                  <tr key={practice.practice_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {practice.practice_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {practice.practice_id.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(practice.subscription_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      ${practice.monthly_fixed_total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      ${practice.current_period_usage_cost.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                      {Math.floor(practice.voice_calls_minutes)} min
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                      {practice.visit_recordings_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                      ${practice.estimated_monthly_total.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Coming Soon - More Features */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Coming Soon</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• View and manage individual practice subscriptions</li>
          <li>• Generate billing reports and exports</li>
          <li>• View detailed usage history</li>
          <li>• Manually report usage to Stripe</li>
          <li>• Configure product catalog and pricing</li>
        </ul>
      </div>
    </div>
  );
};

export default BillingAdminDashboard;

